import { ask, IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';

interface IQueuedRequest {
	id: number;
	label: string;
}

interface IQueueEntry<T> {
	id: number;
	label: string;
	run: () => Promise<T>;
	resolve: (value: T) => void;
	reject: (reason: IAskError) => void;
}

const createAbortError = (): IAskError => ({
	data: new DOMException('Aborted', 'AbortError'),
});

class WebappsCidRequestQueue {
	private pending: IQueueEntry<unknown>[] = [];
	private activeRequests: IQueuedRequest[] = [];
	private running = false;
	private nextId = 0;
	private inFlightController: AbortController | null = null;

	enqueue<T>(label: string, task: () => Promise<T>): Promise<T> {
		const id = ++this.nextId;
		this.activeRequests.push({ id, label });

		return new Promise((resolve, reject) => {
			this.pending.push({
				id,
				label,
				run: task,
				resolve: resolve as (value: unknown) => void,
				reject,
			});
			void this.drain();
		});
	}

	getActiveRequests(): IQueuedRequest[] {
		return [...this.activeRequests];
	}

	abortCurrent(): void {
		this.inFlightController?.abort('external abort');
	}

	clearPending(): void {
		if (this.pending.length === 0) {
			return;
		}

		const abortError = createAbortError();
		const pendingIds = new Set(this.pending.map(entry => entry.id));

		this.pending.forEach(entry => entry.reject(abortError));
		this.pending = [];
		this.activeRequests = this.activeRequests.filter(request => !pendingIds.has(request.id));
	}

	private async drain(): Promise<void> {
		if (this.running) {
			return;
		}

		this.running = true;

		while (this.pending.length > 0) {
			const entry = this.pending.shift() as IQueueEntry<unknown>;
			this.inFlightController = new AbortController();
			ask.addSignal(this.inFlightController.signal);

			try {
				const result = await entry.run();
				entry.resolve(result);
			} catch (error) {
				entry.reject(error as IAskError);
			} finally {
				this.inFlightController = null;
				this.activeRequests = this.activeRequests.filter(request => request.id !== entry.id);
			}
		}

		this.running = false;
	}
}

export const webappsCidRequestQueue = new WebappsCidRequestQueue();
